import { zoneScene, SHAPE_KINDS, shapeDef, shapePath, type ShapeKind, coverPhotoFrame, photoCropRect, cropPhotoRect, designCutoutAssetId, type PhotoCropRect } from '@pz/scene';
import { el, note, slider, textBtn, type DesignerCtx } from './panels.js';
import { observeSize } from '../layers.js';
import { run } from './commands.js';
import { openDialog, closeOverlays } from './overlay.js';

/** Source rectangle, shared crop geometry, and one undoable Apply/Cancel session. */
export function renderCropControl(body: HTMLElement, ctx: DesignerCtx, id: string): () => void {
  const layer = ctx.store.value().layers.find(item => item.id === id);
  if (layer?.kind !== 'image') return () => {};
  const url = (layer.effects?.some(effect => effect.kind === 'removeBackground')
    ? ctx.renderer.assetUrl(designCutoutAssetId(layer.assetId)) : undefined) ?? ctx.renderer.assetUrl(layer.assetId);
  if (!url) { body.append(note(ctx.texts.uploadFailed)); return () => {}; }
  if (!layer.sourcePx) {
    const loading = note(ctx.texts.loading), probe = new Image(); body.append(loading);
    let active = true, cleanup: (() => void) | undefined;
    probe.onload = () => {
      if (!active || !probe.naturalWidth || !probe.naturalHeight || ctx.store.value().layers.find(item => item.id === id) !== layer) return;
      ctx.dispatch({ type: 'update', id, patch: { sourcePx: { w: probe.naturalWidth, h: probe.naturalHeight } }, silent: true });
      loading.remove(); cleanup = renderCropControl(body, ctx, id);
    };
    probe.onerror = () => { if (active) loading.textContent = ctx.texts.uploadFailed; };
    probe.src = url;
    return () => { active = false; probe.onload = null; probe.onerror = null; cleanup?.(); };
  }
  const source = layer.sourcePx;
  let zoomControl: HTMLElement | undefined;
  let alive = true, rotation = layer.crop?.rotation ?? 0, zoomValue = 1;
  let rect = photoCropRect(source, layer.box, layer.crop);
  let ratio: number | undefined;
  let maskShape = layer.maskShape;
  const wrap = el('div', 'pzd-crop-control');
  const presets = el('div', 'pzd-crop-presets');
  const presetLabel = el('label', undefined, 'Odnos stranica');
  const select = el('select'); select.setAttribute('aria-label', 'Odnos stranica');
  for (const [value, label] of [['free', 'Slobodno'], ['original', 'Original'], ['1', '1:1'], ['1.3333333333', '4:3'], ['0.75', '3:4'], ['1.5', '3:2'], ['0.6666666667', '2:3'], ['1.7777777778', '16:9'], ['0.5625', '9:16']]) {
    const option = el('option', undefined, label); option.value = value!; select.append(option);
  }
  presetLabel.append(select); presets.append(presetLabel);
  const maskLabel = el('label', undefined, 'Maska slike'), maskSelect = el('select'); maskSelect.setAttribute('aria-label', 'Maska slike');
  const none = el('option', undefined, 'Bez maske'); none.value = ''; maskSelect.append(none);
  for (const shape of SHAPE_KINDS) { const option = el('option', undefined, shapeDef(shape).labelSr); option.value = shape; maskSelect.append(option); }
  maskSelect.value = maskShape ?? ''; maskLabel.append(maskSelect); presets.append(maskLabel);
  const stage = el('div', 'pzd-crop-stage');
  stage.style.aspectRatio = String(source.w / source.h);
  stage.style.width = `min(100%, ${300 * source.w / source.h}px)`;
  const picture = el('img'); picture.src = url; picture.alt = ''; picture.draggable = false;
  const selection = el('div', 'pzd-crop-selection');
  selection.tabIndex = 0; selection.setAttribute('role', 'group'); selection.setAttribute('aria-label', 'Oblast isecanja');
  selection.setAttribute('aria-description', 'Pomerite oblast ili povucite ručice. Strelice pomeraju oblast; Shift ubrzava.');
  const cropResult = el('div', 'pzd-crop-result'), maskPreview = el('div', 'pzd-crop-mask-preview');
  const resultPhoto = el('img'); resultPhoto.src = url; resultPhoto.alt = ''; resultPhoto.draggable = false;
  maskPreview.append(resultPhoto); cropResult.append(maskPreview);
  selection.append(cropResult, el('div', 'pzd-crop-thirds'));
  const handles: Record<string, string> = { nw: 'Gore levo', n: 'Gornja ivica', ne: 'Gore desno', e: 'Desna ivica', se: 'Dole desno', s: 'Donja ivica', sw: 'Dole levo', w: 'Leva ivica' };
  for (const [handle, label] of Object.entries(handles)) {
    const button = el('button', 'pzd-crop-handle'); button.type = 'button'; button.dataset.cropHandle = handle;
    button.setAttribute('aria-label', `Isecanje: ${label}`); selection.append(button);
  }
  const size = el('output', 'pzd-hint'); size.setAttribute('aria-live', 'polite');
  const imageViewport = el('div', 'pzd-crop-image-viewport'); imageViewport.append(picture);
  const shade = el('div', 'pzd-crop-shade'), shadeWindow = el('div'); shade.append(shadeWindow);
  stage.append(imageViewport, shade, selection);
  const displayRect = () => ({ ...rect, x: layer.flipX ? 1 - rect.x - rect.w : rect.x, y: layer.flipY ? 1 - rect.y - rect.h : rect.y });
  const place = () => {
    const shown = displayRect(), width = stage.clientWidth || 300, height = stage.clientHeight || width * source.h / source.w;
    const maxZoom = 8 / cropPhotoRect(source, rect, layer.box, rotation).crop.zoom;
    zoomValue = Math.min(zoomValue, maxZoom);
    if (zoomControl) {
      const input = zoomControl.querySelector('input')!; input.max = String(Math.floor(maxZoom * 100)); input.value = String(zoomValue * 100); input.disabled = maxZoom <= 1.001;
      const output = zoomControl.querySelector('.pzd-field-val'); if (output) output.textContent = Math.round(zoomValue * 100) + ' %';
    }
    const result = cropPhotoRect(source, rect, layer.box, rotation, zoomValue);
    const frame = coverPhotoFrame(source, { w: shown.w * width, h: shown.h * height }, result.crop);
    const w = source.w * frame.scale, h = source.h * frame.scale;
    picture.style.width = `${w}px`; picture.style.height = `${h}px`;
    picture.style.left = `${(shown.x + shown.w / 2) * width - frame.crop.cx * w}px`;
    picture.style.top = `${(shown.y + shown.h / 2) * height - frame.crop.cy * h}px`;
    picture.style.transformOrigin = `${frame.crop.cx * w}px ${frame.crop.cy * h}px`;
    picture.style.transform = `scale(${layer.flipX ? -1 : 1}, ${layer.flipY ? -1 : 1}) rotate(${frame.rotation}deg)`;
    selection.style.left = `${shown.x * 100}%`; selection.style.top = `${shown.y * 100}%`;
    selection.style.width = `${shown.w * 100}%`; selection.style.height = `${shown.h * 100}%`;
    shadeWindow.style.cssText = selection.style.cssText;
    resultPhoto.style.cssText = picture.style.cssText;
    resultPhoto.style.left = parseFloat(picture.style.left) - shown.x * width + 'px';
    resultPhoto.style.top = parseFloat(picture.style.top) - shown.y * height + 'px';
    const shape = maskShape ? shapeDef(maskShape) : undefined;
    const maskUrl = shape ? 'url("data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1" preserveAspectRatio="none"><path transform="matrix(' + (layer.flipX ? -1 : 1) + ' 0 0 ' + (layer.flipY ? -1 : 1) + ' ' + (layer.flipX ? 1 : 0) + ' ' + (layer.flipY ? 1 : 0) + ')" d="' + shapePath(maskShape!) + '" fill="white" fill-rule="' + (shape.fillRule ?? 'nonzero') + '"/></svg>') + '")' : '';
    maskPreview.style.maskImage = maskUrl; maskPreview.style.webkitMaskImage = maskUrl;
    size.textContent = `${result.sizeMm.w.toFixed(1)} × ${result.sizeMm.h.toFixed(1)} mm`;
  };
  const commit = () => {
    if (!alive) return;
    const current = ctx.store.value().layers.find(item => item.id === id);
    if (current?.kind !== 'image' || current.assetId !== layer.assetId) return;
    run(ctx, { op: 'imageCrop', ids: [id], ...cropPhotoRect(source, rect, layer.box, rotation, zoomValue) }, 'crop:' + id);
  };
  const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
  function change(start: PhotoCropRect, handle: string, dx: number, dy: number): void {
    let left = start.x, top = start.y, right = left + start.w, bottom = top + start.h;
    if (handle === 'move') {
      rect = { ...start, x: clamp(left + dx, 0, 1 - start.w), y: clamp(top + dy, 0, 1 - start.h) };
    } else {
      if (handle.includes('w')) left = clamp(left + dx, 0, right - .125);
      if (handle.includes('e')) right = clamp(right + dx, left + .125, 1);
      if (handle.includes('n')) top = clamp(top + dy, 0, bottom - .125);
      if (handle.includes('s')) bottom = clamp(bottom + dy, top + .125, 1);
      let w = right - left, h = bottom - top;
      if (ratio) {
        const normalizedRatio = ratio * source.h / source.w;
        if (handle === 'n' || handle === 's') w = h * normalizedRatio; else h = w / normalizedRatio;
        const k = Math.min(1, 1 / w, 1 / h); w *= k; h *= k;
        left = clamp(handle.includes('w') ? start.x + start.w - w : left, 0, 1 - w);
        top = clamp(handle.includes('n') ? start.y + start.h - h : top, 0, 1 - h);
      }
      rect = { x: left, y: top, w, h };
    }
    place();
  }
  let drag: { pointer: number; x: number; y: number; rect: PhotoCropRect; handle: string; width: number; height: number } | undefined;
  selection.addEventListener('pointerdown', event => {
    if (event.button !== 0 || drag) return;
    event.preventDefault(); event.stopPropagation(); selection.focus({ preventScroll: true });
    let handle = (event.target as HTMLElement).closest<HTMLElement>('[data-crop-handle]')?.dataset.cropHandle ?? 'move';
    if (layer.flipX) handle = handle.replace(/[we]/g, char => char === 'w' ? 'e' : 'w');
    if (layer.flipY) handle = handle.replace(/[ns]/g, char => char === 'n' ? 's' : 'n');
    drag = { pointer: event.pointerId, x: event.clientX, y: event.clientY, rect: { ...rect }, handle, width: stage.clientWidth, height: stage.clientHeight };
    selection.setPointerCapture(event.pointerId);
  });
  selection.addEventListener('pointermove', event => {
    if (!drag || drag.pointer !== event.pointerId) return;
    change(drag.rect, drag.handle, (event.clientX - drag.x) / drag.width * (layer.flipX ? -1 : 1), (event.clientY - drag.y) / drag.height * (layer.flipY ? -1 : 1));
  });
  const end = (event: PointerEvent) => {
    if (!drag || drag.pointer !== event.pointerId) return;
    const start = drag; drag = undefined;
    if (event.type !== 'pointerup') { rect = start.rect; place(); return; }
    commit();
  };
  selection.addEventListener('pointerup', end); selection.addEventListener('pointercancel', end); selection.addEventListener('lostpointercapture', end);
  selection.addEventListener('keydown', event => {
    const deltas: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const d = deltas[event.key]; if (!d) return;
    event.preventDefault(); event.stopPropagation();
    let handle = (event.target as HTMLElement).dataset.cropHandle ?? 'move';
    if (layer.flipX) handle = handle.replace(/[we]/g, char => char === 'w' ? 'e' : 'w');
    if (layer.flipY) handle = handle.replace(/[ns]/g, char => char === 'n' ? 's' : 'n');
    const step = event.shiftKey ? .03 : .005;
    change(rect, handle, d[0] * step * (layer.flipX ? -1 : 1), d[1] * step * (layer.flipY ? -1 : 1)); commit();
  });
  maskSelect.addEventListener('change', () => {
    maskShape = maskSelect.value ? maskSelect.value as ShapeKind : undefined; place();
    run(ctx, { op: 'imageMask', ids: [id], shape: maskShape ?? null }, 'crop:' + id);
  });
  select.addEventListener('change', () => {
    ratio = select.value === 'free' ? undefined : select.value === 'original' ? source.w / source.h : Number(select.value);
    if (ratio) {
      const r = ratio * source.h / source.w;
      let w = rect.w, h = w / r;
      if (h > rect.h) { h = rect.h; w = h * r; }
      rect = { x: rect.x + (rect.w - w) / 2, y: rect.y + (rect.h - h) / 2, w, h };
    }
    place(); commit();
  });
  const zoom = slider({ label: ctx.texts.zoom, min: 100, max: 400, step: 5, value: 100,
    format: v => Math.round(v) + ' %', onInput: v => { zoomValue = v / 100; place(); }, onCommit: v => { zoomValue = v / 100; place(); commit(); } });
  zoomControl = zoom;
  const angle = slider({ label: ctx.texts.straighten, min: -45, max: 45, step: 1, value: rotation,
    format: v => Math.round(v) + '°', onInput: v => { rotation = v; place(); }, onCommit: v => { rotation = v; place(); commit(); } });
  const reset = textBtn('Vrati celu sliku', () => {
    rect = { x: 0, y: 0, w: 1, h: 1 }; rotation = 0; zoomValue = 1; ratio = undefined; select.value = 'free';
    for (const [control, value, label] of [[zoom, '100', '100 %'], [angle, '0', '0°']] as const) {
      control.querySelector('input')!.value = value;
      const output = control.querySelector('.pzd-field-val'); if (output) output.textContent = label;
    }
    place(); commit();
  }, { icon: 'rotate' });
  wrap.append(presets, stage, note('Povucite ivice da isečete sliku. Pomerite oblast da odaberete deo koji ostaje.'), size, zoom, angle, reset); body.append(wrap);
  // Show the processed source in the framing control, including eraser alpha.
  // The same native renderer supplies the photo; the source-space crop handles
  // remain independent of its display resolution.
  if (layer.erase?.length && ctx.renderer.renderRaw) {
    const w = source.w / 10, h = source.h / 10;
    const scene = zoneScene({ ...ctx.rawConfig, sizeMm: { w, h }, sticker: undefined }, { v: 1, layers: [{ ...layer, crop: undefined, maskShape: undefined, pattern: undefined, flipX: undefined, flipY: undefined, box: { x: w / 2, y: h / 2, w, h } }] }, Math.min(2, 1200 / Math.max(w, h)));
    void ctx.renderer.renderRaw(scene, 1).then(out => {
      if (!alive || !out) return;
      const sourceCanvas = document.createElement('canvas'); sourceCanvas.width = out.width; sourceCanvas.height = out.height;
      sourceCanvas.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(out.pixels), out.width, out.height), 0, 0);
      picture.src = resultPhoto.src = sourceCanvas.toDataURL();
    }).catch(() => { if (alive) ctx.toast('Pregled gumice nije spreman. Ponovo otvorite isecanje.', 'error'); });
  }
  picture.addEventListener('load', place);
  const stop = observeSize(stage, place, 0); place();
  return () => { alive = false; drag = undefined; stop(); picture.removeEventListener('load', place); };
}

export function openCropDialog(ctx: DesignerCtx, id: string): void {
  closeOverlays(ctx);
  const snapshot = ctx.store.state();
  let applied = false;
  openDialog({ ctx, title: ctx.texts.cropTool, className: 'is-crop',
    build: body => renderCropControl(body, ctx, id),
    footer: (row, handle) => row.append(
      textBtn(ctx.texts.cancel, () => handle.close()),
      textBtn(ctx.texts.phApply, () => { applied = true; handle.close(); }, { className: 'pzd-primary' }),
    ),
    onClose: () => ctx.dispatch({ type: 'editSession', snapshot, cancel: !applied }),
  });
}
